import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { DateValidators } from './date.validators';

@Injectable({
  providedIn: 'root',
})
export class InputValidators {
  private static readonly ALPHABETICAL_WILDCARD_REGEX: RegExp = /^[A-Za-z0-9ÂÊÎÔÛÄËÏÖÜÀÇÉÈÙâêîôûäëïöüàçéèù \-']+\*?$/;
  private static readonly ALPHABETICAL_WILDCARD_REGEX2: RegExp = /^\*?[A-Za-z0-9ÂÊÎÔÛÄËÏÖÜÀÇÉÈÙâêîôûäëïöüàçéèù \-']+\*?$/;
  private static readonly ALPHANUM_REGEX: RegExp = /^[A-Za-z0-9]+$/;

  public static readonly PasswordPattern: RegExp = new RegExp('^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[*.!@$%^&(){}[]:;<>,.?/~_+-=|]*).{8,}$');

  /**
   * Validates alphabetical with facultative wildcard format, skip if control is pristine
   * @param control Form control containing input value
   * @returns alphabeticalWildcardFormat error, ortherwise null
   */
  public static alphabeticalWildcardFormatValidator(control: AbstractControl): ValidationErrors | null {
    if (control.pristine) {
      return null;
    }
    control.markAsTouched();
    if (control && (control.value == null || control.value == '')) {
      return null;
    }
    if (InputValidators.ALPHABETICAL_WILDCARD_REGEX.test(control.value)) {
      return null;
    }
    return { alphabeticalWildcardFormat: true };
  }

  /**
   * Validates alphabetical with facultative wildcard format (start with *, end with *), skip if control is pristine
   * @param control Form control containing input value
   * @returns alphabeticalFormat error, ortherwise null
   */
  public static alphabeticalFormatValidator(control: AbstractControl): ValidationErrors | null {
    if (control.pristine) {
      return null;
    }
    control.markAsTouched();
    if (control && (control.value == null || control.value == '')) {
      return null;
    }
    if (InputValidators.ALPHABETICAL_WILDCARD_REGEX2.test(control.value)) {
      return null;
    }
    return { alphabeticalFormat: true };
  }

  /**
   * Validates alphanumeric format, skip if control is pristine
   * @param control Form control containing input value
   * @returns alphanumericFormat error, ortherwise null
   */
  public static alphanumericFormatValidator(control: AbstractControl): ValidationErrors | null {
    if (control.pristine) {
      return null;
    }
    control.markAsTouched();
    if (control && (control.value == null || control.value == '')) {
      return null;
    }
    if (InputValidators.ALPHANUM_REGEX.test(control.value)) {
      return null;
    }
    return { alphanumericFormat: true };
  }

  public static endDateAndReasonValidator(endDateName: string, reasonName: string): (formGroup: UntypedFormGroup) => ValidationErrors | null {
    return (formGroup: UntypedFormGroup): ValidationErrors | null => {
      const endDateCtrl: AbstractControl = formGroup.get(endDateName);
      const reasonCtrl: AbstractControl = formGroup.get(reasonName);

      endDateCtrl.markAsTouched();
      reasonCtrl.markAsTouched();

      if ((endDateCtrl?.value && !reasonCtrl?.value) || (reasonCtrl?.value && !endDateCtrl?.value)) {
        return { endDateAndReason: true };
      }
      return null;
    };
  }

  public static rsjReferentValidator(
    referent: string,
    beginDateName: string,
    endDateName: string,
  ): (formGroup: UntypedFormGroup) => ValidationErrors | null {
    return (formGroup: UntypedFormGroup): ValidationErrors | null => {
      const referentCtrl: AbstractControl = formGroup.get(referent);
      const beginDateCtrl: AbstractControl = formGroup.get(beginDateName);
      const endDateCtrl: AbstractControl = formGroup.get(endDateName);

      referentCtrl.markAsTouched();
      beginDateCtrl.markAsTouched();
      endDateCtrl.markAsTouched();

      if (
        (endDateCtrl && endDateCtrl.value && beginDateCtrl && !beginDateCtrl.value) ||
        (endDateCtrl && endDateCtrl.value && referentCtrl && !referentCtrl.value)
      ) {
        return { rsjReferentFullRequired: true, rsjReferentPartialRequired: false };
      } else if (
        (beginDateCtrl && beginDateCtrl.value && referentCtrl && !referentCtrl.value) ||
        (referentCtrl && referentCtrl.value && beginDateCtrl && !beginDateCtrl.value)
      ) {
        return { rsjReferentPartialRequired: true, rsjReferentFullRequired: false };
      }
      return null;
    };
  }
}
